import React from 'react';

import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { useChangeCoinBanknoteAvailability } from 'src/features/payment/api/useChangeCoinBanknoteAvailability';
import { GET_PAY_STATION_RESOURCE } from 'src/features/payment/api/useGetPayStation';
import { LIST_PAY_STATIONS_CASH_LEVELS_RESOURCE } from 'src/features/payment/api/useListPayStationCashLevels';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import ChangeCashLevelsFormModal from 'src/features/payment/components/forms/ChangeCashLevelsFormModal';
import { PayStation } from 'src/features/payment/utils/types';
import { useParking } from 'src/providers/ParkingProvider';

interface ChangeCashLevelsProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    children?: React.ReactNode;
}

function ChangeCashLevels<TData>({ className, children, selectedPayStation }: ChangeCashLevelsProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useChangeCoinBanknoteAvailability({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_CASH_LEVELS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_PAY_STATION_RESOURCE] });
            toast({
                title: formatMessage(paymentsMessages.cashLevelsChangedSuccess),
                description: formatMessage(paymentsMessages.cashLevelsChangedSuccessDescription, {
                    payStation: selectedPayStation.name,
                }),
            });
        },
    });

    return (
        <ChangeCashLevelsFormModal
            selectedPayStation={selectedPayStation}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ ...data, payStationId: selectedPayStation.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(paymentsMessages.changeCoinBanknoteAvailability)}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(paymentsMessages.changeCoinBanknoteAvailabilitySubtitle, {
                payStation: selectedPayStation.name,
            })}
        >
            {children}
        </ChangeCashLevelsFormModal>
    );
}

export default ChangeCashLevels;
